import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule, NgForm } from '@angular/forms';
import { asyncScheduler, Observable, scheduled } from 'rxjs';

import { ErrorService } from '../../../../../shared/services/error-service';
import { ToastService } from '../../../../../shared/toast/toast.service';
import { WizardStep } from '../../../../../shared/controls/wizard-control/wizard-step.interface';

import { PerformanceRangeMappingInput, PerformanceService, WorkbookService } from '../../../../../../service/proxy';

@Component({
    selector: 'performance-range-selection',
    templateUrl: './performance-range-selection.component.html',
    styleUrls: ['./performance-range-selection.component.scss'],

    imports: [
        CommonModule,
        FormsModule
    ],

    providers: [
        PerformanceService,
        WorkbookService
    ]
})
export class PerformanceRangeSelectionComponent implements OnInit, WizardStep {
    @ViewChild('form')
    form!: NgForm;

    public batchId: number = 0;
    public firstTenRows: any[] = [];
    public lastTenRows: any[] = [];
    public loading: boolean = false;
    public isWideTable = false;

    public model: PerformanceRangeMappingInput = new PerformanceRangeMappingInput();

    constructor(
        private route: ActivatedRoute,
        private performanceService: PerformanceService,
        private workbookService: WorkbookService,
        private toastService: ToastService,
        private errorService: ErrorService,
    ) {}

    ngOnInit(): void {
        // Data loaded in onActivate method
    }

    getBatch(): void {
        this.performanceService.getPerformanceBatch(this.batchId)
            .subscribe((result) => {
                this.getWorksheetSample(`${result.filePath}//${result.storedName}`);
            }
        );
    }

    getImportRowMapping(): void {
        this.performanceService.getPerformanceImportRangeMapping(this.batchId)
            .subscribe((result) => {
                if (result) {
                    this.model = new PerformanceRangeMappingInput({
                        headerIndex: result.headerIndex || 1,
                        startIndex: result.startIndex || 2,
                        endIndex: result.endIndex,
                    });
                }
            }
        );
    }

    getWorksheetSample(filePath: string): void {
        this.loading = true;
        this.isWideTable = false;

        this.workbookService.getSampleBySheetIndex(filePath, 0)
            .subscribe({
                next: (result) => {
                    this.firstTenRows = result.firstTenRows || [];
                    this.lastTenRows = result.lastTenRows || [];

                    const headerRow = this.firstTenRows[0];
                    const colCount = headerRow?.rowValues?.length || 0;
                    this.isWideTable = colCount > 12;

                    // Set endIndex to the last row number (only if not already saved)
                    const lastRow = this.lastTenRows[this.lastTenRows.length - 1];

                    if (lastRow?.rowNumber && !this.model.endIndex) {
                        this.model.endIndex = lastRow.rowNumber;
                    }
                },
                error: (error) => {
                    if (error.status === 400) {
                        this.toastService.show('Bad request. Please try again.', 'info');
                    }
                    else if (error.status === 404) {
                        const errorBody = error.response ? JSON.parse(error.response) : null;
                        if (errorBody?.code === 'FILE_NOT_FOUND') {
                            this.toastService.show('Excel file not found. Delete this import batch and upload again.', 'warning');
                        }
                        else {
                            this.toastService.show('File not found. Please try again.', 'info');
                        }
                    }
                    else {
                        this.errorService.handleError(error);
                    }
                    this.loading = false;
                },
                complete: () => {
                    this.loading = false;
                }
            }
        );
    }

    isValid(): boolean {
        return true;
    }

    onActivate(): void {
        this.route.paramMap
            .subscribe(params => {
                this.batchId = Number(params.get('id'));

                if (this.batchId > 0) {
                    // Always reload the saved range values from the database
                    this.getImportRowMapping();

                    // Only fetch the file preview on first activation.
                    // On back-navigation the rows are already in memory — no need to hit the server again.
                    if (this.firstTenRows.length === 0) {
                        this.getBatch();
                    }
                }
            }
        );
    }

    onNext(): Observable<boolean> {
        return new Observable<boolean>(observer => {
            this.performanceService.createPerformanceImportRangeMapping(this.batchId, this.model)
                .subscribe({
                    next: () => {
                        this.toastService.show('Performance row mapping saved successfully', 'Success');

                        const mappingsChangedKey = `performance_mappings_changed_${this.batchId}`;
                        sessionStorage.setItem(mappingsChangedKey, 'true');

                        observer.next(true);
                        observer.complete();
                    },
                    error: (error: any) => {
                        if (error.status === 400) {
                            this.toastService.show('Bad request. Please try again.', 'Info');
                        }
                        else {
                            this.errorService.handleError(error);
                        }

                        observer.next(false);
                        observer.complete();
                    },
                }
            );
        });
    }

    onPrevious(): Observable<boolean> {
        return scheduled([true], asyncScheduler);
    }
}